// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Category } from "@insite/client-framework/Services/CategoryService";
import {
    GetAlsoPurchasedProductCollectionApiV2Parameter,
    GetProductCollectionApiV2Parameter,
    GetRelatedProductCollectionApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import {
    chain,
    LoadProducts,
    LoadTopSellers,
} from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { BrandModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    carouselId: string;
    carouselType: string;
    relatedProductType: string;
    seedWithManuallyAssigned: string;
    displayProductsFrom: string;
    selectedCategoryIds: string[];
    numberOfProductsToDisplay: number;
    isProductDetailsPage: boolean;
    productId?: string;
    isProductListPage: boolean;
    category?: Category;
    isBrandDetailsPage: boolean;
    brand?: BrandModel;
    isShellEdit?: boolean;
    widgetPosition?: number;
    onComplete?: (props: any) => void;
}

interface Props {
    getProductCollectionParameter?:
        | GetProductCollectionApiV2Parameter
        | GetRelatedProductCollectionApiV2Parameter
        | GetAlsoPurchasedProductCollectionApiV2Parameter;
    extraProductCollectionParameter?:
        | GetProductCollectionApiV2Parameter
        | GetRelatedProductCollectionApiV2Parameter
        | GetAlsoPurchasedProductCollectionApiV2Parameter;
    additionalSort?: (products: ProductModel[]) => ProductModel[];
}
type HandlerType = Handler<Parameter, Props>;

export const XngLoadProducts: HandlerType = ({
    dispatch,
    getProductCollectionParameter,
    extraProductCollectionParameter,
    additionalSort,
    parameter: {
        carouselId,
        carouselType,
        numberOfProductsToDisplay,
        isProductDetailsPage,
        productId,
        isShellEdit,
        onComplete,
    },
}) => {
    if (!getProductCollectionParameter) {
        return;
    }

    const extraProductOptions = extraProductCollectionParameter
        ? {
              getProductCollectionParameter: extraProductCollectionParameter,
              numberOfProductsToDisplay,
          }
        : undefined;

    const isRecentlyViewedOnPDP = carouselType === "recentlyViewed" && isProductDetailsPage;

    // XNG-Change: add properties to the expand
    getProductCollectionParameter.expand
        ? getProductCollectionParameter.expand.push("images", "badges", "properties")
        : (getProductCollectionParameter.expand = ["images", "badges", "properties"]);

    dispatch(
        loadProductInfoList({
            id: carouselId,
            getProductCollectionParameter,
            extraProductOptions,
            excludeProductId: isRecentlyViewedOnPDP ? productId : undefined,
            forceLoad: isRecentlyViewedOnPDP,
            isShellEdit,
            additionalSort,
            // Add on complete
            onComplete,
        }),
    );
};

export const XngLoadTopSellers: typeof LoadTopSellers = props => {
    LoadTopSellers(props);

    props.getProductCollectionParameter = {
        ...props.getProductCollectionParameter,
        includeProductsWithoutCategory: false,
    } as any;
};

replaceInChain(chain, LoadProducts, XngLoadProducts);
replaceInChain(chain, LoadTopSellers, XngLoadTopSellers);
